<template>
  <div class="container py-4">
    <!-- En-tête -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="text-primary fw-bold">
        <i class="bi bi-clipboard-heart me-2"></i> Traitement de la consultation
      </h3>
      <router-link class="btn btn-outline-secondary" :to="{ name: 'consultations_' }">
        <i class="bi bi-arrow-left-circle me-1"></i> Retour à la liste
      </router-link>
    </div>

    <!-- Bloc Infos Patient -->
    <div class="card shadow-sm rounded-4 p-4 mb-4 border-light bg-light">
      <h5 class="mb-3 text-primary fw-bold">
        <i class="bi bi-person-vcard me-2"></i> Informations du patient
      </h5>
      <div class="row">
        <div class="col-md-6 mb-2">
          <p><strong>Nom :</strong> {{ consultation.patient?.nom }} {{ consultation.patient?.prenom }}</p>
          <p><strong>Âge :</strong> {{ calculerAge(consultation.patient?.dateNaissance) }} ans</p>
        </div>
        <div class="col-md-6 mb-2">
          <p><strong>Type :</strong>
            <span class="badge bg-info text-dark">{{ consultation.type?.nom }}</span>
          </p>
          <p><strong>Motif :</strong> {{ consultation.motif }}</p>
        </div>
      </div>
    </div>

    <!-- Diagnostic -->
    <div class="card shadow-sm rounded-4 p-4 mb-4 border-0">
      <h5 class="mb-3 text-success fw-bold"><i class="bi bi-heart-pulse me-2"></i> Diagnostic</h5>
      <textarea
        v-model="consultation.diagnostic"
        rows="4"
        class="form-control"
        placeholder="Entrer le diagnostic..."
      ></textarea>
    </div>

    <!-- Ordonnance -->
    <div class="card shadow-sm rounded-4 p-4 mb-4 border-0">
      <h5 class="mb-3 text-warning fw-bold"><i class="bi bi-capsule-pill me-2"></i> Ordonnance</h5>
      <div
        v-for="(item, index) in ordonnance"
        :key="index"
        class="row g-2 mb-3 align-items-end"
      >
        <div class="col-md-2">
          <label class="form-label">Médicament</label>
          <select class="form-select" v-model="item.medicament_id">
            <option disabled value="">-- Sélectionner un médicament --</option>
            <option
              v-for="med in medicaments"
              :key="med.id"
              :value="med.id"
            >
              {{ med.nom }} (Stock: {{ med.stockActuel }})
            </option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label">Quantité</label>
          <input
            type="number"
            v-model.number="item.quantite"
            class="form-control"
            min="1"
          />
        </div>
        <div class="col-md-2">
          <label class="form-label">Posologie</label>
          <input
            type="text"
            v-model="item.posologie"
            class="form-control"
            placeholder="ex: après repas"
          />
        </div>
        <div class="col-md-2">
          <label class="form-label">Fréquence</label>
          <input
            type="text"
            v-model="item.frequence"
            class="form-control"
            placeholder="ex: 2x/j"
          />
        </div>
        <div class="col-md-2">
          <label class="form-label">Durée</label>
          <input
            type="text"
            v-model="item.duree"
            class="form-control"
            placeholder="ex: 5j"
          />
        </div>
        <div class="col-md-2 text-end">
          <button
            class="btn btn-outline-danger"
            @click="removeMedicament(index)"
            title="Supprimer ce médicament"
          >
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      </div>
      <button class="btn btn-outline-primary" @click="addMedicament">
        <i class="bi bi-plus-circle me-1"></i> Ajouter un médicament
      </button>
    </div>

    <!-- Examens -->
    <div class="card shadow-sm rounded-4 p-4 mb-4 border-0">
      <h5 class="mb-3 text-info fw-bold"><i class="bi bi-file-medical me-2"></i> Examens prescrits</h5>
      <div
        v-for="(exam, index) in examens"
        :key="index"
        class="row g-2 mb-2 align-items-end"
      >
        <div class="col-md-10">
          <input
            type="text"
            v-model="exam.type"
            class="form-control"
            placeholder="Nom de l'examen"
          />
        </div>
        <div class="col-md-2 text-end">
          <button
            class="btn btn-outline-danger"
            @click="removeExamen(index)"
            title="Supprimer cet examen"
          >
            <i class="bi bi-x-circle"></i>
          </button>
        </div>
      </div>
      <button class="btn btn-outline-primary" @click="addExamen">
        <i class="bi bi-plus-circle me-1"></i> Ajouter un examen
      </button>
    </div>

    <!-- Statut et RDV -->
    <div class="card shadow-sm rounded-4 p-4 mb-4 border-0">
      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label">Statut</label>
          <select class="form-select" v-model="consultation.statut">
            <option value="En cours">En cours</option>
            <option value="Terminée">Terminée</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Prochain RDV</label>
          <input
            type="date"
            class="form-control"
            v-model="consultation.prochainRDV"
          />
        </div>
      </div>
    </div>

    <!-- Validation -->
    <div class="text-end">
      <button class="btn btn-success px-4 py-2" @click="sauvegarder">
        <i class="bi bi-check-circle me-2"></i> Valider la consultation
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import dayjs from 'dayjs';

export default {
  data() {
    return {
      consultation: {},
      ordonnance: [],
      examens: [],
      medicaments: [],
    };
  },
  methods: {
    async fetchConsultation() {
      const id = this.$route.params.id;
      try {
        const res = await api.getItem('consultations', id);
        this.consultation = res.data;

        // Charger ordonnance et examens depuis la consultation si existants
        this.ordonnance = this.consultation.ordonnance || [];
        this.examens = this.consultation.examens || [];
      } catch (error) {
        console.error('Erreur lors du chargement de la consultation', error);
        alert('Erreur lors du chargement des données');
      }
    },
    async fetchMedicaments() {
      try {
        const res = await api.getItems('medicaments');
        this.medicaments = res.data.data;
      } catch (error) {
        console.error('Erreur lors du chargement des médicaments', error);
      }
    },
    addMedicament() {
      this.ordonnance.push({
        medicament_id: null,
        quantite: 1,
        posologie: '',
        frequence: '',
        duree: '',
      });
    },
    removeMedicament(index) {
      this.ordonnance.splice(index, 1);
    },
    addExamen() {
      this.examens.push({ type: '' });
    },
    removeExamen(index) {
      this.examens.splice(index, 1);
    },
    calculerAge(dateNaissance) {
      return dateNaissance ? dayjs().diff(dayjs(dateNaissance), 'year') : '-';
    },
    async sauvegarder() {
      try {
        await api.updateItem(
          `consultations/${this.consultation.id}/traitements`,
          '',
          {
            diagnostic: this.consultation.diagnostic,
            observations: this.consultation.observations,
            statut: this.consultation.statut,
            prochainRdv: this.consultation.prochainRDV,
            ordonnance: this.ordonnance.map((item) => ({
              medicament_id: item.medicament_id,
              quantite: item.quantite,
              posologie: item.posologie,
              frequence: item.frequence,
              duree: item.duree,
            })),
            examens: this.examens,
          }
        );
        alert('Consultation mise à jour avec succès.');
        this.$router.push({ name: 'consultations_' });
      } catch (error) {
        console.error('Erreur lors de la sauvegarde', error);
        alert('Erreur lors de l\'enregistrement');
      }
    },
  },
  created() {
    this.fetchConsultation();
    this.fetchMedicaments();
  },
};
</script>

<style scoped>
label {
  font-weight: 600;
}
.card p {
  font-size: 15px;
  color: #333;
}
.badge {
  font-size: 0.9rem;
  padding: 0.4em 0.6em;
}
</style>
